<script>
	//	Public + admin-preview view. SSR draws the full SVG inside a fixed-height box (height is a
	//	function of data + device only), the island adds hover/legend/table/export after mount.
	import { getContext, setContext, onMount, tick } from "svelte";
	import Hydrator from "../../Hydrator.svelte";
	import DomainView from "../../DomainView.svelte";
	import Chart from "../../charts/Chart.svelte";
	import Legend from "../../charts/Legend.svelte";
	import Tooltip from "../../charts/Tooltip.svelte";
	import DataTable from "../../charts/DataTable.svelte";
	import Toolbar from "../../charts/Toolbar.svelte";
	import { normalize, tableRows, summaryText, normalizeUrl, isNum } from "../../charts/model.js";
	import { heightFor, layoutCartesian, layoutPie } from "../../charts/layout.js";
	import { formatValue } from "../../charts/format.js";
	import { seriesVar, ACCENT_VAR, lineDash } from "../../charts/palette.js";
	import { exportPng, copyLink } from "../../charts/exportPng.js";

	export let type = "chart";
	export let data;
	export let id = undefined;

	//	admin mode sends context this way, default to getContext when in renderer mode, pass context below
	export let context = getContext("context");
	setContext("context", context);

	$: if (!data) data = {};
	$: chartType = data.type || "line";
	$: options = data.options || {};
	$: annotations = data.annotations || [];
	$: spec = normalize(data);
	$: pie = chartType === "pie" || chartType === "donut";
	$: isLine = chartType === "line" || chartType === "area";

	//	SSR uses the device hint; the mounted layout follows the actual container.
	//	CSS reserves plot/readout heights before the island runs.
	$: compact = measured ? measured <= 480 : !!($context && $context.client && $context.client.is_mobile);
	$: ssrWidth = compact ? 360 : 640;
	let measured = 0;
	let measuredHeight = 0;
	$: width = measured || ssrWidth;
	$: height = measuredHeight || heightFor(chartType, spec, options, compact);

	let mounted = false;
	let hidden = [];
	let hover = null;
	// Reader interaction is local to the current data; editor updates must not
	// inherit hidden indices from a different set of series or chart type.
	$: if (data) { hidden = []; hover = null; }
	// Own geometry here: SSR cannot read a computed child prop back through bind:layout.
	$: layout = pie
		? layoutPie({ type: chartType, spec, options, width, height, hidden })
		: layoutCartesian({ type: chartType, spec, options, annotations, width, height, hidden, compact });
	let copied = false;
	let busy = false;
	let status = "";
	let inputMode = "pointer";
	let figure;
	let plotEl;
	let copyTimer;

	onMount(() => {
		mounted = true;
		return () => clearTimeout(copyTimer);
	});

	$: legendItems = pie
		? (layout && layout.legend ? layout.legend : []).map((it) => ({ key: it.i, name: it.label, color: it.color, kind: "rect", hidden: hidden.includes(it.i) }))
		: spec.series.map((s, i) => ({ key: i, name: s.name, color: spec.series.length === 1 ? ACCENT_VAR : seriesVar(s.color), kind: isLine ? "line" : "rect", dash: lineDash(i), hidden: hidden.includes(i) }));
	$: showLegend = options.legend !== "none" && (pie ? legendItems.length > 1 : spec.series.length > 1);
	$: legendPos = options.legend === "bottom" ? "bottom" : "top";

	function toggle(key) {
		hover = null;
		if (hidden.includes(key)) hidden = hidden.filter((k) => k !== key);
		else if (legendItems.filter((it) => !it.hidden).length > 1) hidden = [...hidden, key];
	}

	function onHover(h, mode = "pointer") {
		if (!h && mode === "pointer" && inputMode === "keyboard") return;
		hover = h;
		inputMode = mode;
	}

	//	touch readouts stay until a tap lands outside the figure
	function docDown(e) {
		if (mounted && hover && figure && !figure.contains(e.target)) hover = null;
	}

	function tooltipFor(h, L, w) {
		if (!h || !L) return null;
		if (pie) {
			const s = L.slices.find((sl) => sl.i === h.index);
			if (!s) return null;
			return { x: s.lx, y: Math.max(0, s.ly - 12), flip: s.lx > w / 2, title: s.label, rows: [{ value: s.text, color: s.color }, { value: s.pct, name: "din total" }] };
		}
		if (h.kind !== "x" || h.index < 0 || h.index >= spec.x.length) return null;
		const i = h.index;
		const rows = L.visible.map((s) => ({ color: L.single ? ACCENT_VAR : seriesVar(s.color), name: s.name, value: formatValue(s.values[i], options) }));
		if (L.stacked && L.totals && isNum(L.totals[i])) rows.push({ value: formatValue(L.totals[i], options), name: "Total", total: true });
		const pos = L.xAt(i);
		const x = L.horizontal ? L.plot.x + L.plot.w * 0.55 : pos;
		const y = L.horizontal ? pos - 8 : L.plot.y + 6;
		return { x, y, flip: x > w / 2, title: L.catLabels[i], rows };
	}
	$: tooltip = mounted ? tooltipFor(hover, layout, width) : null;
	// The mobile panel is useful before interaction and remains the same size.
	$: readout = tooltip || tooltipFor(pie ? {kind: "mark", index: layout.slices[0]?.i} : {kind: "x", index: isLine ? spec.x.length - 1 : 0}, layout, width);
	$: announcement = tooltip && inputMode !== "pointer"
		? [tooltip.title, ...tooltip.rows.map((r) => `${r.name || "Valoare"}: ${r.value}`)].join(". ") : "";
	$: hasData = pie ? layout.slices.length > 0 : spec.series.some((s) => s.values.some(isNum));
	function docKey(e) {
		if (e.key === "Escape" && figure?.contains(e.target)) hover = null;
	}

	$: alt = (data.alt && data.alt.trim()) || summaryText(data);
	$: table = tableRows(data);
	$: sourceHref = normalizeUrl(data.sourceUrl) || null;

	//	the table twin is server-rendered for small data (works without JS); big tables would
	//	triple the page weight, so they render on first open instead
	const SSR_TABLE_ROWS = 60;
	let tableOpen = false;
	$: tableRendered = table.rows.length <= SSR_TABLE_ROWS || tableOpen;

	function resolveVar(cs, value) {
		const m = /^var\((--[^,)]+)/.exec(value || "");
		return m ? cs.getPropertyValue(m[1]).trim() || "#888" : value;
	}

	async function download() {
		if (busy || !plotEl) return;
		busy = true;
		status = "";
		try {
			hover = null;
			await tick();
			const svg = plotEl.querySelector("svg");
			const cs = getComputedStyle(figure);
			await exportPng({
				svg,
				title: data.title || "",
				subtitle: data.subtitle || "",
				source: data.source || "",
				legend: showLegend ? legendItems.filter((it) => !it.hidden).map((it) => ({ name: it.name, color: resolveVar(cs, it.color), kind: it.kind, dash: it.dash })) : [],
				background: cs.backgroundColor && cs.backgroundColor !== "rgba(0, 0, 0, 0)" ? cs.backgroundColor : "#ffffff",
				font: cs.fontFamily,
				titleFont: figure.querySelector(".chart-title") ? getComputedStyle(figure.querySelector(".chart-title")).fontFamily : cs.fontFamily,
			});
			status = "Imaginea a fost pregătită pentru descărcare.";
		} catch (e) {
			status = "Descărcarea nu a reușit. Încearcă din nou.";
			console.error("[chart] export failed", e);
		} finally {
			busy = false;
		}
	}

	async function copy() {
		if (!id) return;
		copied = await copyLink(id);
		status = copied ? "Link copiat." : "Linkul nu a putut fi copiat. Încearcă din nou.";
		clearTimeout(copyTimer);
		copyTimer = setTimeout(() => (copied = false), 2000);
	}
</script>

<!-- explicit props: the body renderer spreads ad/insert props into blocks, none of that belongs in the island marker -->
<Hydrator props={{ id, type, data }} component="EditorChart" hydrate={!__ADMIN__}>
	<DomainView {type} {data} category="editorBlocks">
		<figure class="container-chart chart-type-{chartType}" id={id ? `chart-${id}` : undefined} bind:this={figure}>
			{#if data.title || data.subtitle}
				<header class="chart-head">
					{#if data.title}<p class="chart-title">{data.title}</p>{/if}
					{#if data.subtitle}<p class="chart-subtitle">{data.subtitle}</p>{/if}
				</header>
			{/if}
			{#if showLegend && legendPos === "top"}
				<Legend items={legendItems} onToggle={toggle} interactive={mounted} />
			{/if}
			<div class="chart-plot" bind:this={plotEl} bind:clientWidth={measured} bind:clientHeight={measuredHeight} style="--plot-height:{heightFor(chartType, spec, options, false)}px; --plot-small-height:{heightFor(chartType, spec, options, true)}px">
				{#if hasData}
				<Chart
					type={chartType}
					{spec}
					{width}
					{height}
					{hover}
					{alt}
					interactive={mounted}
					onHover={mounted ? onHover : null}
					{layout}
				/>
				{:else}
					<div class="chart-empty">Nu sunt disponibile date pentru acest grafic.</div>
				{/if}
				{#if tooltip}
					<div class="chart-hover"><Tooltip x={tooltip.x} y={tooltip.y} flip={tooltip.flip} title={tooltip.title} rows={tooltip.rows} {width} {height} onLeave={() => onHover(null)} /></div>
				{/if}
			</div>
			{#if showLegend && legendPos === "bottom"}
				<Legend items={legendItems} onToggle={toggle} interactive={mounted} />
			{/if}
			{#if hasData}
				<!-- svelte-ignore a11y_no_noninteractive_tabindex (This bounded region must support keyboard scrolling.) -->
				<div class="chart-readout" style="--readout-height:{pie ? 132 : Math.min(180, 84 + spec.series.length * 24)}px" tabindex="0" role="region" aria-label="Valorile selectate">
					<span class="chart-hint">{mounted ? "Atinge graficul pentru alte valori." : "Valorile complete sunt disponibile în tabel."}</span>
					{#if readout}<Tooltip inline title={readout.title} rows={readout.rows} />{/if}
				</div>
				<div class="chart-keyboard-hint" class:pending={!mounted}>Explorează valorile cu cursorul sau cu săgețile de pe tastatură.</div>
			{/if}
			<span class="chart-sr" aria-live="polite" aria-atomic="true">{announcement}</span>
			<div class="chart-foot">
				{#if data.source}
					<p class="chart-source">
						Sursa:
						{#if sourceHref && !__ADMIN__}<a href={sourceHref} rel="nofollow noopener" target="_blank">{data.source}</a>{:else}{data.source}{/if}
					</p>
				{/if}
				{#if table.header.length - 1 > (pie ? 1 : spec.series.length)}
					<p class="chart-source">Graficul afișează {pie ? "prima serie" : `primele ${spec.series.length} serii`}. Toate seriile sunt disponibile în tabelul de date.</p>
				{/if}
			</div>
			<div class="chart-actions">
				{#if table.rows.length}
					<details class="chart-data" on:toggle={(e) => (tableOpen = e.currentTarget.open)}>
						<summary>Vezi datele</summary>
						{#if tableRendered}
							<DataTable header={table.header} rows={table.rows} caption={data.title || ""} />
						{:else}
							<noscript>Activează JavaScript pentru a vedea tabelul complet.</noscript>
						{/if}
					</details>
				{/if}
				{#if !__ADMIN__}
					<div class="chart-export"><Toolbar onDownload={download} onCopy={copy} {copied} canCopy={!!id} {busy} ready={mounted && hasData} /></div>
				{/if}
			</div>
			<div class="chart-status" role="status" aria-live="polite" aria-atomic="true">{status}</div>
			{#if data.caption}
				<figcaption class="caption"><span class="captionText">{data.caption}</span></figcaption>
			{/if}
		</figure>
	</DomainView>
</Hydrator>

<svelte:document on:pointerdown={docDown} on:keydown={docKey} />

<style lang="scss">
	.container-chart {
		/* Public brand tokens resolve here for SSR, islands, and editor previews. */
		--_s1: var(--chart-s1, #2669ad);
		--_s2: var(--chart-s2, #b85120);
		--_s3: var(--chart-s3, #197b5b);
		--_s4: var(--chart-s4, #956d00);
		--_s5: var(--chart-s5, #ab4772);
		--_s6: var(--chart-s6, #49692e);
		--_s7: var(--chart-s7, #7355b5);
		--_s8: var(--chart-s8, #ba4044);
		--_other: var(--chart-other, #737373);
		--_pie-label: var(--chart-pie-label, #fff);
		--_accent: var(--chart-accent, var(--_s1));
		--_wash: color-mix(in srgb, var(--_ink) 4%, var(--_surface));
		--_ink: var(--chart-ink, #1a1a1a);
		--_ink-muted: var(--chart-ink-muted, #6b6b6b);
		--_grid: var(--chart-grid, #e5e5e5);
		--_axis: var(--chart-axis, #b8b8b8);
		--_surface: var(--chart-surface, #ffffff);
		--_font: var(--chart-font, system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif);
		--_title-font: var(--chart-title-font, var(--_font));
		--_num-font: var(--chart-num-font, var(--_font));

		display: block;
		width: 100%;
		min-width: 0;
		box-sizing: border-box;
		margin: 32px 0;
		padding: 20px 0 0;
		border-top: 1px solid var(--_grid);
		container-type: inline-size;
		color: var(--_ink);
		background: var(--_surface);
		font-family: var(--_font);
		font-size: 14px;
		line-height: 1.4;
		scroll-margin-top: var(--chart-anchor-offset, 72px);
	}

	/* explicit dark theme only: brands without a theme toggle must not flip on OS preference */
	:global(:root[data-theme="dark"]) .container-chart {
		--_s1: var(--chart-s1, #75ace6);
		--_s2: var(--chart-s2, #f19b6c);
		--_s3: var(--chart-s3, #59b799);
		--_s4: var(--chart-s4, #d9b654);
		--_s5: var(--chart-s5, #e495b6);
		--_s6: var(--chart-s6, #a2ba75);
		--_s7: var(--chart-s7, #b1a0ed);
		--_s8: var(--chart-s8, #ec9291);
		--_other: var(--chart-other, #7a7a7a);
		--_pie-label: var(--chart-pie-label, #15171b);
		--_ink: var(--chart-ink, #ececec);
		--_ink-muted: var(--chart-ink-muted, #a8a8a8);
		--_grid: var(--chart-grid, #2e2e2e);
		--_axis: var(--chart-axis, #4a4a4a);
		--_surface: var(--chart-surface, #1a1a19);
	}

	.container-chart:target {
		box-shadow: 0 0 0 2px var(--_accent);
		border-radius: 4px;
	}

	.chart-head { margin: 0 0 20px; overflow-wrap: anywhere; }
	.chart-title {
		margin: 0;
		font: 700 24px / 1.2 var(--_title-font);
		letter-spacing: -0.015em;
		color: var(--_ink);
		text-wrap: pretty;
	}
	.chart-subtitle { margin: 8px 0 0; font-size: 14px; line-height: 1.5; color: var(--_ink-muted); }
	.chart-plot { height: var(--plot-height); position: relative; width: 100%; min-width: 0; }
	.chart-empty { height: 100%; display: grid; place-items: center; color: var(--_ink-muted); text-align: center; }
	.chart-readout { display: none; }
	.chart-hint { display: block; margin-bottom: 8px; font-size: 12px; }
	.chart-keyboard-hint { margin: 12px 0; font-size: 12px; color: var(--_ink-muted); }
	.chart-keyboard-hint.pending { visibility: hidden; }
	.chart-status { font-size: 13px; color: var(--_ink-muted); margin-top: 8px; }
	.chart-status:empty { display: none; }
	.chart-sr { position: absolute; width: 1px; height: 1px; overflow: hidden; clip-path: inset(50%); }
	.chart-foot { border-top: 1px solid var(--_grid); padding-top: 12px; margin-top: 16px; }
	.chart-source { margin: 0 0 4px; font-size: 13px; line-height: 1.5; color: var(--_ink-muted); overflow-wrap: anywhere; }
	.chart-source a { color: inherit; text-decoration: underline; text-underline-offset: 3px; }
	.chart-actions { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 0 12px; align-items: start; }
	.chart-export { grid-area: 1 / 2; max-width: 270px; }
	.chart-data { grid-area: 1 / 1 / 2 / -1; min-width: 0; font-size: 14px; }
	.chart-data summary {
		cursor: pointer;
		color: var(--_ink);
		font-size: 13px;
		list-style: none;
		display: flex;
		width: max-content;
		max-width: calc(100% - 282px);
		min-height: 44px;
		box-sizing: border-box;
		align-items: center;
		gap: 10px;
		padding: 8px 4px;
		border-radius: 3px;
	}
	.chart-data summary::-webkit-details-marker { display: none; }
	.chart-data summary::before {
		content: "";
		flex: 0 0 6px;
		height: 6px;
		border-right: 1.5px solid currentColor;
		border-bottom: 1.5px solid currentColor;
		transform: rotate(-45deg);
		margin-left: 2px;
	}
	.chart-data[open] summary::before { transform: rotate(45deg); }
	.chart-data summary:hover { background: var(--_wash); }
	.chart-data summary:focus-visible, .chart-readout:focus-visible { outline: 2px solid var(--_accent); outline-offset: 2px; }
	.caption { margin: 12px 0 0; font-size: 13px; line-height: 1.5; color: var(--_ink-muted); overflow-wrap: anywhere; }
	.captionText { display: inline; min-width: 0; }
	@container (max-width: 480px) {
		.chart-plot { height: var(--plot-small-height); }
		.chart-title { font-size: 21px; }
		.chart-head { margin-bottom: 16px; }
		.chart-hover, .chart-keyboard-hint { display: none; }
		.chart-readout {
			display: block;
			box-sizing: border-box;
			height: var(--readout-height);
			overflow: auto;
			margin-top: 12px;
			padding: 12px;
			background: var(--_wash);
			border-radius: 4px;
			font-size: 14px;
			line-height: 1.5;
			color: var(--_ink-muted);
		}
		.chart-export { grid-area: 2 / 1 / 3 / -1; max-width: none; }
		.chart-data summary { max-width: 100%; }
	}
	@media (prefers-reduced-motion: no-preference) {
		.chart-data summary::before { transition: transform 0.12s; }
	}
</style>
